<app-page-layout
  pageTitle="API Catalogue"
  subtitle="Every route can belong to one or more manageable API groups."
>
  <!-- Toast -->
  <p-toast></p-toast>

  <!-- Actions Bar -->
  <div page-actions class="flex items-center gap-2">
    <p-button
      label="Register API"
      icon="pi pi-plus"
      (onClick)="openRegisterApi()"
      severity="primary"
      size="small"
      outlined
    ></p-button>
  </div>

  <!-- Filter Bar -->
  <div class="flex justify-between items-center mb-4">
    <div class="flex items-center gap-3">
      <!-- Search -->
      <div class="relative w-72">
        <p-iconField iconPosition="left" class="w-72">
          <p-inputIcon styleClass="pi pi-search"></p-inputIcon>
          <input
            type="text"
            pInputText
            placeholder="Filter APIs..."
            [(ngModel)]="filterText"
            (input)="applyFilter()"
            class="w-full"
          />
        </p-iconField>
      </div>

      <!-- Group Filter Dropdown -->
      <div class="relative w-56">
        <p-select
          [(ngModel)]="groupFilter"
          [options]="groupOptions()"
          optionLabel="label"
          optionValue="value"
          (ngModelChange)="applyFilter()"
          class="w-full"
          placeholder="All groups"
          appendTo="body"
          [showClear]="true"
        >
        </p-select>
      </div>

      <!-- Status Filter Dropdown -->
      <div class="relative w-48">
        <p-select
          [(ngModel)]="statusFilter"
          [options]="statusFilterOptions"
          optionLabel="label"
          optionValue="value"
          (ngModelChange)="applyFilter()"
          class="w-full"
          placeholder="All statuses"
          [showClear]="true"
        >
        </p-select>
      </div>
    </div>
  </div>

  <!-- State Container -->
  <app-state-container
    [state]="viewState()"
    loadingMessage="Loading APIs..."
    loadingSubMessage="Please wait while we fetch your APIs"
    emptyIcon="pi-server"
    emptyTitle="No APIs found"
    [emptyMessage]="emptyStateMessage()"
    [showEmptyAction]="true"
    emptyActionLabel="Register API"
    emptyActionIcon="pi-plus"
    [emptyActionOutlined]="true"
    [showClearFilters]="showClearFilters()"
    [errorMessage]="errorMessage()"
    (emptyAction)="openRegisterApi()"
    (clearFilters)="clearFilters()"
    (retry)="retryLoad()"
  >
    <!-- Table -->
    <div class="bg-white dark:bg-zinc-950 rounded-lg border border-surface-200 dark:border-zinc-800">
      <p-table
        [value]="filteredApis()"
        [loading]="loading()"
        dataKey="id"
        class="p-datatable-sm"
        [resizableColumns]="true"
        columnResizeMode="fit"
        [rowHover]="true"
        sortMode="single"
        (onSort)="onSort($event)"
        [paginator]="true"
        [rows]="pageSize"
        [totalRecords]="totalRecords()"
        (onPage)="onPageChange($event)"
        paginatorPosition="bottom"
        [first]="currentPage * pageSize"
      >
        <ng-template pTemplate="header">
          <tr class="bg-surface-100 dark:bg-zinc-900 text-surface-600">
            <th
              (click)="onSort({field:'method'})"
              pResizableColumn
              style="min-width:100px;"
              class="cursor-pointer group bg-surface-100 dark:bg-zinc-900"
            >
              <div class="flex items-center justify-between text-surface-700 dark:text-zinc-200">
                <span>Method</span>
                <i
                  class="pi pi-arrow-down transform transition-all duration-200"
                  [ngClass]="{
                    'opacity-0 group-hover:opacity-100': sortField !== 'method' || sortOrder === 0,
                    'rotate-180 opacity-100': sortField === 'method' && sortOrder === -1,
                    'opacity-100': sortField === 'method' && sortOrder === 1
                  }"
                ></i>
              </div>
            </th>
            <th
              (click)="onSort({field:'name'})"
              pResizableColumn
              style="min-width:180px;"
              class="cursor-pointer group bg-surface-100 dark:bg-zinc-900"
            >
              <div class="flex items-center justify-between text-surface-700 dark:text-zinc-200">
                <span>API Name</span>
                <i
                  class="pi pi-arrow-down transform transition-all duration-200"
                  [ngClass]="{
                    'opacity-0 group-hover:opacity-100': sortField !== 'name' || sortOrder === 0,
                    'rotate-180 opacity-100': sortField === 'name' && sortOrder === -1,
                    'opacity-100': sortField === 'name' && sortOrder === 1
                  }"
                ></i>
              </div>
            </th>
            <th
              (click)="onSort({field:'gatewayRoute'})"
              pResizableColumn
              style="min-width:200px;"
              class="cursor-pointer group bg-surface-100 dark:bg-zinc-900"
            >
              <div class="flex items-center justify-between text-surface-700 dark:text-zinc-200">
                <span>Route</span>
                <i
                  class="pi pi-arrow-down transform transition-all duration-200"
                  [ngClass]="{
                    'opacity-0 group-hover:opacity-100': sortField !== 'gatewayRoute' || sortOrder === 0,
                    'rotate-180 opacity-100': sortField === 'gatewayRoute' && sortOrder === -1,
                    'opacity-100': sortField === 'gatewayRoute' && sortOrder === 1
                  }"
                ></i>
              </div>
            </th>
            <th
              (click)="onSort({field:'primaryGroup'})"
              pResizableColumn
              style="min-width:180px;"
              class="cursor-pointer group bg-surface-100 dark:bg-zinc-900"
            >
              <div class="flex items-center justify-between text-surface-700 dark:text-zinc-200">
                <span>Primary Group</span>
                <i
                  class="pi pi-arrow-down transform transition-all duration-200"
                  [ngClass]="{
                    'opacity-0 group-hover:opacity-100': sortField !== 'primaryGroup' || sortOrder === 0,
                    'rotate-180 opacity-100': sortField === 'primaryGroup' && sortOrder === -1,
                    'opacity-100': sortField === 'primaryGroup' && sortOrder === 1
                  }"
                ></i>
              </div>
            </th>
            <th
              (click)="onSort({field:'requiredScope'})"
              pResizableColumn
              style="min-width:150px;"
              class="cursor-pointer group bg-surface-100 dark:bg-zinc-900"
            >
              <div class="flex items-center justify-between text-surface-700 dark:text-zinc-200">
                <span>Required Scope</span>
                <i
                  class="pi pi-arrow-down transform transition-all duration-200"
                  [ngClass]="{
                    'opacity-0 group-hover:opacity-100': sortField !== 'requiredScope' || sortOrder === 0,
                    'rotate-180 opacity-100': sortField === 'requiredScope' && sortOrder === -1,
                    'opacity-100': sortField === 'requiredScope' && sortOrder === 1
                  }"
                ></i>
              </div>
            </th>
            <th
              (click)="onSort({field:'status'})"
              pResizableColumn
              style="min-width:120px;"
              class="cursor-pointer group bg-surface-100 dark:bg-zinc-900"
            >
              <div class="flex items-center justify-between text-surface-700 dark:text-zinc-200">
                <span>Status</span>
                <i
                  class="pi pi-arrow-down transform transition-all duration-200"
                  [ngClass]="{
                    'opacity-0 group-hover:opacity-100': sortField !== 'status' || sortOrder === 0,
                    'rotate-180 opacity-100': sortField === 'status' && sortOrder === -1,
                    'opacity-100': sortField === 'status' && sortOrder === 1
                  }"
                ></i>
              </div>
            </th>
            <th
              pResizableColumn
              style="min-width:100px;"
              class="text-center bg-surface-100 dark:bg-zinc-900 text-surface-700 dark:text-zinc-200"
            >
              Actions
            </th>
          </tr>
        </ng-template>

        <ng-template pTemplate="body" let-api>
          <tr class="border-t border-surface-200 dark:border-surface-700 hover:bg-surface-50 dark:hover:bg-surface-800 transition cursor-pointer" (click)="viewApiDetails(api)">
            <td class="py-2">
              <span
                class="px-2 py-1 rounded text-[11px] font-bold inline-flex"
                [ngClass]="getMethodClasses(api.method)"
              >
                {{ api.method }}
              </span>
            </td>
            <td class="py-2 text-surface-700 dark:text-surface-300 font-medium">
              {{ api.name }}
            </td>
            <td class="py-2 text-surface-700 dark:text-surface-300">
              <code class="rounded bg-surface-100 dark:bg-surface-800 px-2 py-1 font-mono text-xs text-surface-700 dark:text-surface-200">
                {{ api.gatewayRoute }}
              </code>
            </td>
            <td class="py-2 text-surface-600 dark:text-surface-300">
              {{ api.primaryGroup.name }}
            </td>
            <td class="py-2">
              <span
                class="inline-flex rounded-md bg-surface-100 dark:bg-surface-800 px-2 py-1 font-mono text-[11px] text-surface-700 dark:text-surface-200"
              >
                {{ api.requiredScope }}
              </span>
            </td>
            <td class="py-2">
              <span
                class="px-2 py-0.5 rounded text-xs inline-flex"
                [ngClass]="getStatusClasses(api.status)"
              >
                {{ api.status }}
              </span>
            </td>
            <td class="py-2">
              <div class="flex items-center justify-center gap-2" (click)="$event.stopPropagation()">
                <p-button
                  icon="pi pi-pencil"
                  severity="secondary"
                  size="small"
                  outlined
                  (onClick)="editApi(api)"
                  pTooltip="Edit API"
                  tooltipPosition="top"
                ></p-button>
              </div>
            </td>
          </tr>
        </ng-template>

        <ng-template pTemplate="emptymessage">
          <tr>
            <td colspan="7" class="text-center py-8 text-surface-500 dark:text-surface-400">
              No APIs found matching your filters
            </td>
          </tr>
        </ng-template>
      </p-table>
    </div>
  </app-state-container>
</app-page-layout>

<!-- View/Register/Edit API Dialog -->
<p-dialog
  [(visible)]="showRegisterApiDialog"
  [style]="{ width: '650px', maxWidth: '95vw' }"
  [header]="viewMode ? 'API Details' : (isEditMode ? 'Edit API' : 'Register API')"
  [modal]="true"
  [closable]="true"
  (onHide)="closeRegisterApi()"
  [draggable]="false"
  [resizable]="false"
>
  <div class="py-2">
    <!-- Form fields in grid layout -->
    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
      <!-- API Name -->
      <div class="flex flex-col gap-1.5">
        <label for="apiName" class="text-sm font-medium text-surface-700 dark:text-surface-200">
          API name @if (!viewMode) { <span class="text-red-500">*</span> }
        </label>
        <input
          id="apiName"
          pInputText
          [(ngModel)]="newApi.name"
          placeholder="e.g. Customer Invoice API"
          class="w-full"
          [disabled]="viewMode"
        />
      </div>

      <!-- Environment -->
      <div class="flex flex-col gap-1.5">
        <label for="environment" class="text-sm font-medium text-surface-700 dark:text-surface-200">Environment</label>
        <p-select
          id="environment"
          [options]="environmentOptions"
          [(ngModel)]="newApi.environment"
          optionLabel="label"
          optionValue="value"
          appendTo="body"
          placeholder="Select environment"
          styleClass="w-full"
          [disabled]="viewMode"
        ></p-select>
      </div>

      <!-- Method -->
      <div class="flex flex-col gap-1.5">
        <label for="method" class="text-sm font-medium text-surface-700 dark:text-surface-200">Method</label>
        <p-select
          id="method"
          [options]="methodOptions"
          [(ngModel)]="newApi.method"
          optionLabel="label"
          optionValue="value"
          appendTo="body"
          placeholder="Select method"
          styleClass="w-full"
          [disabled]="viewMode"
        ></p-select>
      </div>

      <!-- Gateway Route -->
      <div class="flex flex-col gap-1.5">
        <label for="route" class="text-sm font-medium text-surface-700 dark:text-surface-200">
          Gateway route @if (!viewMode) { <span class="text-red-500">*</span> }
        </label>
        <input
          id="route"
          pInputText
          [(ngModel)]="newApi.gatewayRoute"
          placeholder="/v1/invoices"
          class="w-full"
          [disabled]="viewMode"
        />
      </div>

      <!-- Upstream Service URL (full width) -->
      <div class="flex flex-col gap-1.5 md:col-span-2">
        <label for="upstreamUrl" class="text-sm font-medium text-surface-700 dark:text-surface-200">
          Upstream service URL @if (!viewMode) { <span class="text-red-500">*</span> }
        </label>
        <input
          id="upstreamUrl"
          pInputText
          [(ngModel)]="newApi.upstreamServiceUrl"
          placeholder="https://sap-integration.internal/api/invoices"
          class="w-full"
          [disabled]="viewMode"
        />
      </div>

      <!-- Primary API Group -->
      <div class="flex flex-col gap-1.5">
        <label for="primaryGroup" class="text-sm font-medium text-surface-700 dark:text-surface-200">
          Primary API group @if (!viewMode) { <span class="text-red-500">*</span> }
        </label>
        <p-select
          id="primaryGroup"
          [options]="groupOptions()"
          [(ngModel)]="newApi.primaryGroupId"
          optionLabel="label"
          optionValue="value"
          placeholder="Select group"
          appendTo="body"
          styleClass="w-full"
          [disabled]="viewMode"
        ></p-select>
      </div>

      <!-- Authentication -->
      <div class="flex flex-col gap-1.5">
        <label for="authentication" class="text-sm font-medium text-surface-700 dark:text-surface-200">Authentication</label>
        <p-select
          id="authentication"
          [options]="authenticationOptions"
          [(ngModel)]="newApi.authentication"
          optionLabel="label"
          optionValue="value"
          placeholder="Select authentication"
          appendTo="body"
          styleClass="w-full"
          [disabled]="viewMode"
        ></p-select>
      </div>

      <!-- Registration Status -->
      <div class="flex flex-col gap-1.5">
        <label for="status" class="text-sm font-medium text-surface-700 dark:text-surface-200">Registration status</label>
        <p-select
          id="status"
          [options]="statusOptions"
          [(ngModel)]="newApi.status"
          optionLabel="label"
          optionValue="value"
          placeholder="Select status"
          styleClass="w-full"
          appendTo="body"
          [disabled]="viewMode"
        ></p-select>
      </div>
    </div>

    <!-- Notice -->
    <div class="mt-4 rounded-lg border border-primary-100 bg-primary-50 p-3 text-sm text-primary-700 dark:border-primary-500/20 dark:bg-primary-500/10 dark:text-primary-300">
      <span class="font-medium">ℹ️</span> The gateway will validate the selected authentication method and required scopes before forwarding requests to the upstream service. Configure rate limits, timeouts, and path handling in the API Group settings.
    </div>
  </div>

  <ng-template pTemplate="footer">
    <div class="flex justify-end gap-2">
      <p-button
        [label]="viewMode ? 'Close' : 'Cancel'"
        severity="secondary"
        (onClick)="closeRegisterApi()"
      ></p-button>
      @if (!viewMode) {
        <p-button
          [label]="isEditMode ? 'Update API' : 'Register API'"
          severity="primary"
          (onClick)="saveNewApi()"
          [loading]="loading()"
        ></p-button>
      }
    </div>
  </ng-template>
</p-dialog>